import { Cell, Grid, HFlow, Icon, isEqual } from 'bold-ui'
import CheckPermission from 'components/auth/CheckPermission'
import { CheckboxField, Form, FormDebouncedValueSpy, TextField } from 'components/form'
import { PopperButton, PopperControls } from 'components/popper'
import { TipoExameEnum } from 'graphql/types.generated'
import { useFilterCounter } from 'hooks/useFilterCounter'
import { useMemo } from 'react'
import { metaPath } from 'util/metaPath'

import { grupoCboSolicitacaoExame } from '../acessos'
import { SolicitacaoExameFilterModel } from './ExamesTable'
import { SolicitacaoExamesFilterPopper, SolicitacaoExamesFilterPopperModel } from './ExamesTableFilterPopperForm'

export interface SolicitacaoExamesTableFilterProps {
  dataInicioAtendimento: Date
  initialValues?: SolicitacaoExameFilterModel
  onChange(values: SolicitacaoExameFilterModel): void
}

const path = metaPath<SolicitacaoExameFilterModel>()

const DEFAULT_TIPOS: TipoExameEnum[] = [TipoExameEnum.COMUM, TipoExameEnum.ALTO_CUSTO, TipoExameEnum.OCI]

const getDefaultPopperValues = (): SolicitacaoExamesFilterPopperModel => ({
  tipoExames: DEFAULT_TIPOS,
  periodo: null,
  profissional: null,
  cbo: null,
})

export default function SolicitacaoExameTableFilter(props: SolicitacaoExamesTableFilterProps) {
  const { dataInicioAtendimento, initialValues, onChange } = props

  const popperValues: SolicitacaoExamesFilterPopperModel = useMemo(() => {
    if (!initialValues) return getDefaultPopperValues()
    const { tipoExames, periodo, profissional, cbo } = initialValues as SolicitacaoExamesFilterPopperModel
    return {
      tipoExames: tipoExames ?? [],
      periodo,
      profissional,
      cbo,
    }
  }, [initialValues])

  const isDefaultFilter = useMemo(() => {
    const normalized = normalizeExamesForDefault(popperValues)
    const normalizedDefault = getNormalizedDefaultExamesFilter()
    return isEqual(normalized, normalizedDefault)
  }, [popperValues])

  const handleOnClear = () => {
    const defaults = getDefaultPopperValues()
    onChange({
      ...initialValues,
      ...defaults,
    } as SolicitacaoExameFilterModel)
  }

  const renderPopper = (ctrl: PopperControls) => (
    <SolicitacaoExamesFilterPopper
      dataInicioAtendimento={dataInicioAtendimento}
      initialValues={popperValues}
      onSubmit={onChange}
      onClose={ctrl.close}
      onClear={handleOnClear}
      isDefaultFilter={isDefaultFilter}
    />
  )

  const contadorFiltroPadrao: SolicitacaoExameFilterModel = useMemo(
    () => ({
      tipoExames: DEFAULT_TIPOS,
      ...initialValues,
    }),
    [initialValues]
  )

  const countFiltrosAplicados = useFilterCounter(contadorFiltroPadrao, {
    query: false,
    somenteMeus: false,
  })

  const renderForm = () => (
    <>
      <FormDebouncedValueSpy onChange={(formState) => onChange(formState.values)} wait={300} />
      <Grid gap={0.5} alignItems='center' justifyContent='space-evenly'>
        <Cell size={5}>
          <TextField name={path.query} placeholder='Pesquise por solicitação' icon='zoomOutline' />
        </Cell>
        <Cell flexGrow={1} alignSelf='center'>
          <CheckPermission permission={grupoCboSolicitacaoExame.adicionar}>
            <CheckboxField name={path.somenteMeus} label='Ver somente as minhas solicitações' />
          </CheckPermission>
        </Cell>
        <Cell alignSelf='flex-end'>
          <PopperButton kind='primary' size='small' skin='default' placement='bottom-end' renderPopper={renderPopper}>
            <HFlow hSpacing={0.5}>
              <Icon icon='filterOutline' />
              {'Filtros' + (countFiltrosAplicados > 0 ? ` (${countFiltrosAplicados})` : '')}
            </HFlow>
          </PopperButton>
        </Cell>
      </Grid>
    </>
  )

  return <Form<SolicitacaoExameFilterModel> onSubmit={onChange} render={renderForm} initialValues={initialValues} />
}

const byTipoExameOrder = (tipoA: TipoExameEnum, tipoB: TipoExameEnum): number => {
  const indiceA = DEFAULT_TIPOS.indexOf(tipoA)
  const indiceB = DEFAULT_TIPOS.indexOf(tipoB)

  if (indiceA === -1 || indiceB === -1) {
    return String(tipoA).localeCompare(String(tipoB))
  }
  return indiceA - indiceB
}

const normalizeExamesForDefault = (solicitacoes: SolicitacaoExamesFilterPopperModel) => ({
  tipoExames: (solicitacoes.tipoExames ?? []).slice().sort(byTipoExameOrder),
  periodo:
    solicitacoes.periodo && (solicitacoes.periodo.startDate || solicitacoes.periodo.endDate)
      ? solicitacoes.periodo
      : null,
  profissional: solicitacoes.profissional ?? null,
  cbo: solicitacoes.cbo ?? null,
})

const getNormalizedDefaultExamesFilter = () => ({
  tipoExames: DEFAULT_TIPOS.slice().sort(byTipoExameOrder),
  periodo: null,
  profissional: null,
  cbo: null,
})
